import type { ActivityType } from '@/data/idp/interfaces';
import type { ActivityStatus, PlanStatus } from '@/data/idp/plans/interfaces';
import type { RecommendationPlanIds } from '@/data/idp/profile-based-recommendations/interfaces';

export type IdpCreationMode = 'ai' | 'manual';

export type IdpFeedbackOption = 'current' | 'new' | 'skip';

export type IdpResourceSource = 'recommended' | 'search' | 'external';

export interface IdpPlanProperties {
  plan_id?: string;
  plan_status?: PlanStatus;
  creation_mode?: IdpCreationMode;
  duration_months?: number | null;
  activities_count?: number;
  activities_completed?: number;
}

export interface IdpActivityProperties extends IdpPlanProperties {
  activity_type?: ActivityType;
  generated_by_ai: boolean;
}

export enum IdpEvent {
  PlanCreationStarted = 'IDP Plan Creation Started',
  WizardStepCompleted = 'IDP Wizard Step Completed',
  AiFailed = 'IDP AI Failed',
  RecommendationRedone = 'IDP Recommendation Redone',
  WizardExited = 'IDP Wizard Exited',
  PlanCreated = 'IDP Plan Created',
  PlanStarted = 'IDP Plan Started',
  PlanStartBlocked = 'IDP Plan Start Blocked',
  PlanFinished = 'IDP Plan Finished',
  PlanRestarted = 'IDP Plan Restarted',
  PlanDeleted = 'IDP Plan Deleted',
  PlanEdited = 'IDP Plan Edited',
  ActivityCreated = 'IDP Activity Created',
  ActivityEdited = 'IDP Activity Edited',
  ActivityStatusChanged = 'IDP Activity Status Changed',
  ActivityDeleted = 'IDP Activity Deleted',
  ActivitiesReordered = 'IDP Activities Reordered',
  ActivityResourceAttached = 'IDP Activity Resource Attached',
  ActivityCourseClicked = 'IDP Activity Course Clicked',
}

export interface IdpEventProperties {
  [IdpEvent.PlanCreationStarted]: { creation_mode: IdpCreationMode };
  [IdpEvent.WizardStepCompleted]: {
    plan_id?: string;
    step: RecommendationPlanIds;
    duration_months?: number;
    feedback_option?: IdpFeedbackOption;
    feedback_length?: number;
    skills_count?: number;
    feedback_skills_count?: number;
  };
  [IdpEvent.AiFailed]: {
    plan_id?: string;
    stage: 'feedback_processing' | 'plan_generation';
  };
  [IdpEvent.RecommendationRedone]: { plan_id?: string };
  [IdpEvent.WizardExited]: {
    plan_id?: string;
    step: RecommendationPlanIds;
    action: 'continue_later' | 'discard';
  };
  [IdpEvent.PlanCreated]: {
    plan_id?: string;
    creation_mode: IdpCreationMode;
    duration_months?: number | null;
    skills_count?: number;
    activities_suggested?: number;
    activities_kept?: number;
    courses_attached?: number;
  };
  [IdpEvent.PlanStarted]: IdpPlanProperties;
  [IdpEvent.PlanStartBlocked]: IdpPlanProperties & {
    reason: 'missing_requirements' | 'another_in_progress';
  };
  [IdpEvent.PlanFinished]: IdpPlanProperties & {
    days_since_start?: number;
  };
  [IdpEvent.PlanRestarted]: IdpPlanProperties;
  [IdpEvent.PlanDeleted]: IdpPlanProperties;
  [IdpEvent.PlanEdited]: IdpPlanProperties & {
    fields_changed: ('title' | 'description' | 'duration')[];
  };
  [IdpEvent.ActivityCreated]: IdpActivityProperties;
  [IdpEvent.ActivityEdited]: IdpActivityProperties & {
    fields_changed: ('name' | 'description' | 'resource')[];
  };
  [IdpEvent.ActivityStatusChanged]: IdpActivityProperties & {
    from: ActivityStatus;
    to: ActivityStatus;
    has_resource: boolean;
  };
  [IdpEvent.ActivityDeleted]: IdpActivityProperties;
  [IdpEvent.ActivitiesReordered]: IdpPlanProperties & {
    moved_count: number;
  };
  [IdpEvent.ActivityResourceAttached]: IdpActivityProperties & {
    source: IdpResourceSource;
  };
  [IdpEvent.ActivityCourseClicked]: IdpActivityProperties & {
    course_id: number | string;
  };
}
